import { Box, createStyles, makeStyles, Typography } from "@material-ui/core";
import { useFormik } from "formik";
import { startCase } from "lodash";
import * as yup from "yup";

import {
  Button,
  MoneyInput,
  OutlinedTextField,
  TwoNumberInput,
} from "components";
import { CredentialSubmissionFormValues } from "services/credentialSubmission/types";
import { textFieldPurpleStyles } from "theme/constants";

const useStyles = makeStyles(() =>
  createStyles({
    inputRoot: textFieldPurpleStyles,
  }),
);

type FormValues = Pick<
  CredentialSubmissionFormValues,
  | "attestedLtm"
  | "attestedAge"
  | "biggestSongName"
  | "biggestSongArtistName"
  | "password"
>;

interface FormProps {
  onSave: (values: FormValues) => Promise<void>;
  royaltyPayorDisplayName: string;
  royaltyPayorUsername: string;
  username: string;
  isUpdatingCredentialSubmission: boolean;
  initialValues: FormValues;
}

const CredentialSubmissionDetailsForm: React.FC<FormProps> = props => {
  const {
    username,
    royaltyPayorDisplayName,
    royaltyPayorUsername,
    isUpdatingCredentialSubmission,
  } = props;

  const classes = useStyles();

  const validationSchema = yup.object().shape({
    password: isUpdatingCredentialSubmission
      ? yup.string().required("Required")
      : yup.string(),
    attestedLtm: yup.string().required("Required"),
    attestedAge: yup.number().required("Required"),
    biggestSongName: yup.string().max(255, "Must be 255 characters or less"),
    biggestSongArtistName: yup
      .string()
      .max(255, "Must be 255 characters or less"),
  });

  const {
    touched,
    values,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    isValid,
  } = useFormik<FormValues>({
    initialValues: props.initialValues,
    validationSchema,
    onSubmit: async values => {
      try {
        await props.onSave(values);
      } catch {
        // pass
      }
    },
  });

  return (
    <form onSubmit={handleSubmit}>
      {isUpdatingCredentialSubmission && (
        <>
          <Box my={2} textAlign="left">
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {startCase(royaltyPayorUsername ?? "username")}
            </Typography>
            <Typography variant="body1" color="textPrimary">
              {username}
            </Typography>
          </Box>
          <Box my={2}>
            <OutlinedTextField
              data-attr="credential-submission-update-password"
              className={classes.inputRoot}
              label="Password"
              name="password"
              type="password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.password && !!errors.password}
              helperText={touched.password && errors.password}
              disabled={isSubmitting}
              autoFocus
            />
          </Box>
        </>
      )}
      <Box my={2}>
        <OutlinedTextField
          data-attr="credential-submission-attested-ltm"
          autoFocus={!isUpdatingCredentialSubmission}
          className={classes.inputRoot}
          label={`Your yearly ${royaltyPayorDisplayName} earnings`}
          name="attestedLtm"
          placeholder="$"
          value={values.attestedLtm}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.attestedLtm && !!errors.attestedLtm}
          helperText={touched.attestedLtm && errors.attestedLtm}
          disabled={isSubmitting}
          InputProps={{ inputComponent: MoneyInput }}
        />
      </Box>
      <Box my={2}>
        <OutlinedTextField
          data-attr="credential-submission-attested-age"
          className={classes.inputRoot}
          label={`How long you’ve been with ${royaltyPayorDisplayName}`}
          name="attestedAge"
          type="text"
          placeholder="3.5"
          value={values.attestedAge}
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!touched.attestedAge && !!errors.attestedAge}
          helperText={
            !!touched.attestedAge && !!errors.attestedAge && errors.attestedAge
          }
          disabled={isSubmitting}
          InputProps={{
            endAdornment: "years",
            inputComponent: TwoNumberInput,
          }}
        />
      </Box>
      <Box my={2}>
        <OutlinedTextField
          data-attr="credential-submission-biggest-song-name"
          className={classes.inputRoot}
          label={`Name of your biggest song with ${royaltyPayorDisplayName} (optional)`}
          name="biggestSongName"
          value={values.biggestSongName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.biggestSongName && !!errors.biggestSongName}
          helperText={touched.biggestSongName && errors.biggestSongName}
          disabled={isSubmitting}
        />
      </Box>
      <Box my={2}>
        <OutlinedTextField
          data-attr="credential-submission-biggest-song-artist-name"
          className={classes.inputRoot}
          label={`Name of the artist who recorded your biggest song with ${royaltyPayorDisplayName} (optional)`}
          name="biggestSongArtistName"
          value={values.biggestSongArtistName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={
            touched.biggestSongArtistName && !!errors.biggestSongArtistName
          }
          helperText={
            touched.biggestSongArtistName && errors.biggestSongArtistName
          }
          disabled={isSubmitting}
        />
      </Box>
      <Box display="flex" flexDirection="row-reverse" width="100%">
        <Button
          data-attr="credential-submission-submit-details-button"
          customColor="green"
          variant="contained"
          disabled={isSubmitting || !isValid}
          type="submit"
        >
          Next
        </Button>
      </Box>
    </form>
  );
};

export default CredentialSubmissionDetailsForm;
